import { refreshSessionAccessToken } from 'src/helper/api_helper';
import { readChatSessionIdentity, useAuthSessionRevision } from 'src/auth/sessionEvents';
// React provider that owns the admin chat Socket.IO singleton (#400,
// Sprint 1 of Chat Epic #393).
//
// Replaces the previous module-scope `io.connect(...)` in
// `Messenger/index.tsx` so the connection lifecycle (sign-in, sign-out,
// token refresh) is bound to a real component tree.
//
// Mount this provider at the Messenger route only — login screens and
// other pre-auth flows must NOT hold an open socket open against the
// gateway (the handshake would just fail).
//
// Auth storage notifications reconnect the socket on token rotation. A changed
// principal remounts retained thread state; a refreshed token keeps unsent drafts.

import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'react-toastify';

import { createChatSocket, type ChatSocket } from './chatSocket';

interface ChatSocketContextValue {
  socket: ChatSocket | null;
  isConnected: boolean;
  /**
   * Connection error surfaced by the gateway (e.g. expired token, missing
   * participant row). Consumers can render a banner instead of leaving the
   * UI silently broken.
   */
  lastError: string | null;
}

const emptySocketContext: ChatSocketContextValue = { socket: null, isConnected: false, lastError: null };
const ChatSocketContext = createContext<ChatSocketContextValue | null>(null);

function readAccessToken(): string {
  try {
    const raw = localStorage.getItem('PLS');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    // Older sessions stored the token at the top level; newer ones expose
    // an `accessToken` field. Tolerate both rather than break a half-rolled-out
    // upgrade.
    return parsed?.accessToken || parsed?.user?.accessToken || '';
  } catch {
    return '';
  }
}

interface ChatSocketProviderProps {
  children: ReactNode;
}

export function ChatSocketProvider({ children }: ChatSocketProviderProps) {
  const parent = useContext(ChatSocketContext);
  // Nested quote tabs share the workspace connection and its auth lifecycle.
  return parent ? <>{children}</> : <OwnedChatSocketProvider>{children}</OwnedChatSocketProvider>;
}

function OwnedChatSocketProvider({ children }: ChatSocketProviderProps) {
  const sessionRevision = useAuthSessionRevision();
  const identity = readChatSessionIdentity(false);
  const [socket, setSocket] = useState<ChatSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  // Hold the live socket in a ref so cleanup can dispose without racing
  // against React state updates that may not have flushed yet.
  const socketRef = useRef<ChatSocket | null>(null);
  const authRecoveryAttempted = useRef(false);

  useEffect(() => {
    const token = readAccessToken();
    if (!token) {
      // Surface the missing-token state instead of throwing — login screens
      // navigate here briefly during sign-in before the storage write lands.
      setLastError('Missing access token; chat is unavailable.');
      return undefined;
    }

    let active = true;
    let s: ChatSocket | null = null;
    try {
      s = createChatSocket(token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open chat socket';
      setLastError(message);
      return undefined;
    }

    socketRef.current = s;
    setSocket(s);

    const onConnect = () => {
      if (!active) return;
      authRecoveryAttempted.current = false;
      setIsConnected(true);
      setLastError(null);
    };
    const onDisconnect = () => {
      if (!active) return;
      setIsConnected(false);
    };
    const onConnectError = async (err: Error) => {
      if (!active) return;
      const reason =
        err && typeof err.message === 'string'
          ? err.message
          : 'Chat connection failed';
      if (/invalid.*token|expired.*token|token.*expired|unauthorized/i.test(reason) && !authRecoveryAttempted.current) {
        authRecoveryAttempted.current = true;
        try {
          await refreshSessionAccessToken();
          // The session notification rebuilds the socket with the fresh token.
          return;
        } catch {
          if (!active) return;
        }
      }
      if (!active) return;
      setLastError(reason);
      // Console log mirrors the previous behaviour of the inline handler in
      // Messenger/index.tsx so existing debugging muscle-memory still works.
      // eslint-disable-next-line no-console
      console.error(`[chat] connect_error: ${reason}`);
      toast.error(`Chat disconnected: ${reason}`, {
        toastId: 'chat-connection-error',
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
      });
    };

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onConnectError);

    return () => {
      active = false;
      try {
        s?.off('connect', onConnect);
        s?.off('disconnect', onDisconnect);
        s?.off('connect_error', onConnectError);
        // `disconnect()` issues the FIN; `close()` also tears down the
        // underlying engine.io transport. Either is fine; pick one
        // consistently to avoid double-event firing.
        s?.disconnect();
      } catch {
        // The socket lib swallows duplicate disconnect calls; nothing to do.
      }
      if (socketRef.current === s) {
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
    };
    // Auth storage writes notify this provider. Cleanup disposes the previous
    // socket before reconnect; only principal changes remount retained drafts.
  }, [sessionRevision]);

  const value = useMemo<ChatSocketContextValue>(
    () => ({ socket, isConnected, lastError }),
    [socket, isConnected, lastError],
  );

  return (
    <ChatSocketContext.Provider value={value}>
      <Fragment key={identity}>{children}</Fragment>
    </ChatSocketContext.Provider>
  );
}

/**
 * Hook accessor for components living under <ChatSocketProvider/>. Returns
 * the live socket or `null` if the provider hasn't created one yet (e.g.
 * pre-handshake, missing token).
 */
export function useChatSocket(): ChatSocketContextValue {
  return useContext(ChatSocketContext) ?? emptySocketContext;
}
